import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../../components/ui/dialog";
import { 
  Shield, Users, Search, Eye, Edit, Trash2, LogIn, LogOut,
  ChevronLeft, ChevronRight, CheckCircle, XCircle, Loader2, Mail, CreditCard
} from "lucide-react";

export const AdminUsersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Modals
  const [selectedUser, setSelectedUser] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [impersonateModalOpen, setImpersonateModalOpen] = useState(false);
  
  // Edit form
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  
  const navigate = useNavigate();
  const adminToken = localStorage.getItem("admin_token");
  const adminUser = JSON.parse(localStorage.getItem("admin_user") || "{}");

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${adminToken}` },
        params: { page, page_size: 20, search: search || undefined }
      });
      setUsers(response.data.users);
      setTotalPages(response.data.total_pages);
      setTotal(response.data.total);
    } catch (error) {
      if (error.response?.status === 403 || error.response?.status === 401) {
        localStorage.removeItem("admin_token");
        navigate("/admin");
      }
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  }, [adminToken, page, search, navigate]);

  useEffect(() => {
    if (!adminToken) {
      navigate("/admin");
      return;
    }
    fetchUsers();
  }, [adminToken, navigate, fetchUsers]);

  const handleViewUser = async (user) => {
    try {
      const response = await axios.get(`${API}/admin/users/${user.user_id}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setSelectedUser(response.data);
      setViewModalOpen(true);
    } catch (error) {
      toast.error("Failed to load user details");
    }
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setEditForm({
      name: user.name || "",
      email: user.email || "",
      email_verified: user.email_verified || false,
      is_admin: user.is_admin || false,
      new_password: "",
      subscription_plan: user.subscription?.plan || "free"
    });
    setEditModalOpen(true);
  };

  const handleSaveUser = async () => {
    setSaving(true);
    try {
      const updateData = { ...editForm };
      const planToUpdate = updateData.subscription_plan;
      delete updateData.subscription_plan;
      
      if (!updateData.new_password) {
        delete updateData.new_password;
      }
      
      // Update user info
      await axios.put(`${API}/admin/users/${selectedUser.user_id}`, updateData, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      
      // Update subscription if changed
      const currentPlan = selectedUser.subscription?.plan || "free";
      if (planToUpdate !== currentPlan) {
        await axios.put(
          `${API}/admin/users/${selectedUser.user_id}/subscription`,
          { plan: planToUpdate },
          { headers: { Authorization: `Bearer ${adminToken}` } }
        );
      }
      
      toast.success("User updated successfully");
      setEditModalOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update user");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async () => {
    try {
      await axios.delete(`${API}/admin/users/${selectedUser.user_id}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      
      toast.success("User deleted successfully");
      setDeleteModalOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  const handleImpersonate = async () => {
    try {
      const response = await axios.post(
        `${API}/admin/users/${selectedUser.user_id}/impersonate`,
        {},
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      
      // Store the impersonation token in the regular user storage
      localStorage.setItem("token", response.data.access_token);
      localStorage.setItem("user", JSON.stringify(response.data.user));
      
      toast.success(`Now logged in as ${response.data.user.email}`);
      setImpersonateModalOpen(false);
      
      // Redirect to the main app
      window.location.href = "/";
    } catch (error) {
      toast.error("Failed to impersonate user");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_user");
    navigate("/admin");
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  };

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">Admin Portal</h1>
              <p className="text-sm text-slate-400">Campaign Pro</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-slate-400 text-sm">{adminUser.email}</span>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleLogout}
              className="text-slate-400 hover:text-white hover:bg-slate-700"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-slate-800/50 border-b border-slate-700 px-6 py-2">
        <div className="max-w-7xl mx-auto flex gap-4">
          <Link 
            to="/admin/dashboard"
            className="px-4 py-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg text-sm font-medium transition-colors"
          >
            Dashboard
          </Link>
          <Link 
            to="/admin/users"
            className="px-4 py-2 text-white bg-slate-700 rounded-lg text-sm font-medium"
          >
            Users
          </Link>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-white">User Management</h2>
            <p className="text-slate-400">{total} total users</p>
          </div>
          <div className="relative w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input
              placeholder="Search by name or email..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
            />
          </div>
        </div>

        {/* Users Table */}
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-3 px-4 text-slate-400 font-medium text-sm">User</th>
                    <th className="text-left py-3 px-4 text-slate-400 font-medium text-sm">Status</th>
                    <th className="text-left py-3 px-4 text-slate-400 font-medium text-sm">Plan</th>
                    <th className="text-left py-3 px-4 text-slate-400 font-medium text-sm">Registered</th>
                    <th className="text-left py-3 px-4 text-slate-400 font-medium text-sm">Stats</th>
                    <th className="text-right py-3 px-4 text-slate-400 font-medium text-sm">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={6} className="py-8 text-center text-slate-400">
                        <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                      </td>
                    </tr>
                  ) : users.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="py-8 text-center text-slate-400">
                        No users found
                      </td>
                    </tr>
                  ) : (
                    users.map((user) => (
                      <tr key={user.user_id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center">
                              {user.picture ? (
                                <img src={user.picture} alt="" className="w-10 h-10 rounded-full" />
                              ) : (
                                <span className="text-white font-medium">
                                  {(user.name || user.email || "?")[0].toUpperCase()}
                                </span>
                              )}
                            </div>
                            <div>
                              <p className="text-white font-medium flex items-center gap-2">
                                {user.name || "Unnamed"}
                                {user.is_admin && (
                                  <Badge className="bg-red-500/20 text-red-400 text-xs">Admin</Badge>
                                )}
                              </p>
                              <p className="text-slate-400 text-sm">{user.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          {user.email_verified ? (
                            <Badge className="bg-emerald-500/20 text-emerald-400">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Verified
                            </Badge>
                          ) : (
                            <Badge className="bg-amber-500/20 text-amber-400">
                              <XCircle className="w-3 h-3 mr-1" />
                              Unverified
                            </Badge>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <Badge className={
                            user.subscription?.plan === "professional" ? "bg-purple-500/20 text-purple-400" :
                            user.subscription?.plan === "growth" ? "bg-blue-500/20 text-blue-400" :
                            user.subscription?.plan === "starter" ? "bg-sky-500/20 text-sky-400" :
                            "bg-slate-500/20 text-slate-400"
                          }>
                            {(user.subscription?.plan || "free").charAt(0).toUpperCase() + (user.subscription?.plan || "free").slice(1)}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-slate-300 text-sm">
                          {formatDate(user.created_at)}
                        </td>
                        <td className="py-3 px-4">
                          <div className="text-sm text-slate-400">
                            <span className="text-white">{user.stats?.contacts_count || 0}</span> contacts •{" "}
                            <span className="text-white">{user.stats?.campaigns_sent || 0}</span> campaigns sent
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-slate-400 hover:text-white hover:bg-slate-700"
                              onClick={() => handleViewUser(user)}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-slate-400 hover:text-white hover:bg-slate-700"
                              onClick={() => handleEditUser(user)}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-slate-400 hover:text-blue-400 hover:bg-slate-700"
                              onClick={() => {
                                setSelectedUser(user);
                                setImpersonateModalOpen(true);
                              }}
                            >
                              <LogIn className="w-4 h-4" />
                            </Button>
                            {user.user_id !== adminUser.user_id && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-slate-400 hover:text-red-400 hover:bg-slate-700"
                                onClick={() => {
                                  setSelectedUser(user);
                                  setDeleteModalOpen(true);
                                }}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-slate-700">
                <p className="text-slate-400 text-sm">
                  Page {page} of {totalPages}
                </p>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={page <= 1}
                    onClick={() => setPage(p => p - 1)}
                    className="border-slate-600 text-slate-300"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={page >= totalPages}
                    onClick={() => setPage(p => p + 1)}
                    className="border-slate-600 text-slate-300"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* View User Modal */}
      <Dialog open={viewModalOpen} onOpenChange={setViewModalOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle>User Details</DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-6">
              {/* User Info */}
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-slate-700 flex items-center justify-center">
                  {selectedUser.user?.picture ? (
                    <img src={selectedUser.user.picture} alt="" className="w-16 h-16 rounded-full" />
                  ) : (
                    <span className="text-2xl text-white font-medium">
                      {(selectedUser.user?.name || selectedUser.user?.email || "?")[0].toUpperCase()}
                    </span>
                  )}
                </div>
                <div>
                  <h3 className="text-xl font-bold">{selectedUser.user?.name || "Unnamed"}</h3>
                  <p className="text-slate-400">{selectedUser.user?.email}</p>
                  <div className="flex gap-2 mt-1">
                    <Badge className={selectedUser.user?.email_verified ? "bg-emerald-500/20 text-emerald-400" : "bg-amber-500/20 text-amber-400"}>
                      {selectedUser.user?.email_verified ? "Verified" : "Unverified"}
                    </Badge>
                    {selectedUser.user?.is_admin && (
                      <Badge className="bg-red-500/20 text-red-400">Admin</Badge>
                    )}
                    <Badge className="bg-slate-600 text-slate-300">
                      {selectedUser.user?.auth_provider === "google" ? "Google" : "Email"}
                    </Badge>
                  </div>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-slate-700/50 rounded-lg p-4">
                  <p className="text-2xl font-bold">{selectedUser.stats?.contacts_count || 0}</p>
                  <p className="text-slate-400 text-sm">Contacts</p>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-4">
                  <p className="text-2xl font-bold">{selectedUser.stats?.campaigns_sent || 0}</p>
                  <p className="text-slate-400 text-sm">Campaigns Sent</p>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-4">
                  <p className="text-2xl font-bold">{selectedUser.stats?.emails_sent || 0}</p>
                  <p className="text-slate-400 text-sm">Emails Sent</p>
                </div>
              </div>

              {/* Outscraper Searches */}
              {selectedUser.outscraper_jobs?.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">Recent Searches</h4>
                  <div className="bg-slate-700/50 rounded-lg divide-y divide-slate-600">
                    {selectedUser.outscraper_jobs.slice(0, 5).map((job) => (
                      <div key={job.id || `${job.category}-${job.location}-${job.created_at}`} className="px-4 py-2 flex justify-between">
                        <span>{job.category} in {job.location}</span>
                        <span className="text-slate-400 text-sm">{job.contacts_added || 0} contacts</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Billing Info */}
              <div className="bg-slate-700/50 rounded-lg p-4">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <CreditCard className="w-4 h-4" />
                  Subscription
                </h4>
                <div className="flex items-center justify-between">
                  <div>
                    <Badge className={
                      selectedUser.subscription?.plan === "professional" ? "bg-purple-500/20 text-purple-400" :
                      selectedUser.subscription?.plan === "growth" ? "bg-blue-500/20 text-blue-400" :
                      selectedUser.subscription?.plan === "starter" ? "bg-sky-500/20 text-sky-400" :
                      "bg-slate-500/20 text-slate-400"
                    }>
                      {(selectedUser.subscription?.plan || "free").charAt(0).toUpperCase() + (selectedUser.subscription?.plan || "free").slice(1)} Plan
                    </Badge>
                    {selectedUser.subscription?.admin_assigned && (
                      <span className="ml-2 text-xs text-amber-400">(Admin assigned)</span>
                    )}
                  </div>
                  <span className="text-slate-400 text-sm">
                    {selectedUser.subscription?.plan === "professional" ? "£100/mo" :
                     selectedUser.subscription?.plan === "growth" ? "£50/mo" :
                     selectedUser.subscription?.plan === "starter" ? "£20/mo" : "Free"}
                  </span>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit User Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-slate-400">Name</label>
              <Input
                value={editForm.name || ""}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                className="mt-1 bg-slate-700 border-slate-600"
              />
            </div>
            <div>
              <label className="text-sm text-slate-400">Email</label>
              <Input
                type="email"
                value={editForm.email || ""}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                className="mt-1 bg-slate-700 border-slate-600"
              />
            </div>
            <div>
              <label className="text-sm text-slate-400">New Password (leave blank to keep current)</label>
              <Input
                type="password"
                value={editForm.new_password || ""}
                onChange={(e) => setEditForm({ ...editForm, new_password: e.target.value })}
                placeholder="Enter new password"
                className="mt-1 bg-slate-700 border-slate-600"
              />
            </div>
            <div>
              <label className="text-sm text-slate-400">Subscription Plan</label>
              <select
                value={editForm.subscription_plan || "free"}
                onChange={(e) => setEditForm({ ...editForm, subscription_plan: e.target.value })}
                className="mt-1 w-full h-10 px-3 rounded-md bg-slate-700 border border-slate-600 text-white focus:outline-none focus:ring-2 focus:ring-sky-500"
              >
                <option value="free">Free - £0/month</option>
                <option value="starter">Starter - £20/month</option>
                <option value="growth">Growth - £50/month</option>
                <option value="professional">Professional - £100/month</option>
              </select>
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={editForm.email_verified || false}
                  onChange={(e) => setEditForm({ ...editForm, email_verified: e.target.checked })}
                  className="rounded border-slate-600"
                />
                <span className="text-sm">Email Verified</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={editForm.is_admin || false}
                  onChange={(e) => setEditForm({ ...editForm, is_admin: e.target.checked })}
                  className="rounded border-slate-600"
                />
                <span className="text-sm">Admin Access</span>
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveUser} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-red-400">Delete User</DialogTitle>
            <DialogDescription className="text-slate-400">
              Are you sure you want to delete <strong>{selectedUser?.email}</strong>? 
              This will permanently delete all their data including contacts, templates, and campaigns.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteUser}>
              Delete User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Impersonate Confirmation Modal */}
      <Dialog open={impersonateModalOpen} onOpenChange={setImpersonateModalOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>Login as User</DialogTitle>
            <DialogDescription className="text-slate-400">
              You will be logged in as <strong>{selectedUser?.email}</strong>. 
              You can use this to provide support or debug issues.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setImpersonateModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleImpersonate} className="bg-blue-600 hover:bg-blue-700">
              <LogIn className="w-4 h-4 mr-2" />
              Login as User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUsersPage;
